import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Drawer,
  Input,
  Select,
  Switch,
  useDrawer,
} from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Search, Tags } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SUPPORTED_COUNTRIES } from '@/common/constants/countries'
import { LANGUAGES } from '@/common/constants/languages'
import { capitalizeFirstLetter } from '@/common/utils/capitalizeFirstLetter'
import { useFilterAccounts } from '../hooks/useFilterAccounts'
import {
  FilterAccountsForm,
  FilterAccountsSchema,
} from '../schemas/FilterAccountsSchema'

export function DrawerFilterAccounts() {
  const { t, i18n } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-filter-accounts')
  const [_, setFilters] = useFilterAccounts()
  const hookForm = useForm<FilterAccountsForm>({
    resolver: zodResolver(FilterAccountsSchema()),
    defaultValues: {
      freeSearch: null,
      country: null,
      locale: null,
      tag: null,
      isActive: false,
    },
  })

  const handleSubmit = (data: FilterAccountsForm) => {
    setFilters(data)
    close()
  }

  const languageName = new Intl.DisplayNames([i18n.language], {
    type: 'language',
  })

  const regionName = new Intl.DisplayNames([i18n.language], {
    type: 'region',
  })

  const LANGUAGE_OPTIONS = LANGUAGES.map((language) => ({
    label: capitalizeFirstLetter(languageName.of(language) || ''),
    value: language,
  }))

  const COUNTRY_OPTIONS = SUPPORTED_COUNTRIES.map((country) => ({
    label: regionName.of(country) || '',
    value: country,
  }))

  return (
    <Drawer.Root id="drawer-filter-accounts">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:accounts.drawer_filter.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:accounts.drawer_filter.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <Input.Icon
              icon={<Search />}
              name="freeSearch"
              label={t('sso:accounts.drawer_filter.fields.free_search.label')}
              placeholder={t(
                'sso:accounts.drawer_filter.fields.free_search.placeholder',
              )}
            />

            <Input.Icon
              icon={<Tags />}
              name="tag"
              label={t('sso:accounts.drawer_filter.fields.tag.label')}
              placeholder={t(
                'sso:accounts.drawer_filter.fields.tag.placeholder',
              )}
            />

            <Select.Single
              name="locale"
              label={t('sso:accounts.drawer_filter.fields.locale.label')}
              placeholder={t(
                'sso:accounts.drawer_filter.fields.locale.placeholder',
              )}
              options={LANGUAGE_OPTIONS}
            />

            <Select.Single
              name="country"
              label={t('sso:accounts.drawer_filter.fields.country.label')}
              placeholder={t(
                'sso:accounts.drawer_filter.fields.country.placeholder',
              )}
              options={COUNTRY_OPTIONS}
            />

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="isActive">
                {t('sso:accounts.drawer_filter.fields.is_active.label')}
              </Switch.Label>
              <Switch.Trigger name="isActive" />
            </Switch.Root>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-between gap-4">
          <Button.Root
            variant="secondary"
            color="brand"
            onClick={close}
            type="button"
          >
            {t('sso:accounts.drawer_filter.footer.button_close.label')}
          </Button.Root>

          <div className="flex items-center gap-4">
            <Button.Root
              variant="secondary"
              color="danger"
              onClick={() => hookForm.reset()}
            >
              {t('sso:accounts.drawer_filter.footer.reset_button.label')}
            </Button.Root>

            <Button.Root
              variant="primary"
              color="brand"
              onClick={hookForm.handleSubmit(handleSubmit)}
              type="submit"
            >
              {t('sso:accounts.drawer_filter.footer.submit_button.label')}
            </Button.Root>
          </div>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
